/**
 * PricingCalculatorIntegrationDetail
 *
 * Displays detailed pricing breakdown for a selected integration including:
 * - Setup fee with discount
 * - Annual recurring fee with discount
 * - Migration checkbox
 * - Pipeline opportunities (for first-time integrations)
 *
 * @see src/modules/RetoolModule/types/pricingCalculator.ts
 * @see src/modules/RetoolModule/components/PricingCalculatorForm.tsx
 */

import React from 'react';
import styled from 'styled-components';
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  InputAdornment,
  Slider,
  Tooltip,
  IconButton,
} from '@mui/material';
import { HelpCircle as HelpIcon, X as CloseIcon, Truck as MigrationIcon } from 'lucide-react';
import {
  calculateIntegrationSetupFee,
  calculateIntegrationRecurringFee,
  calculateIntegrationSetupHours,
  INTEGRATION_METADATA,
} from '../types/pricingCalculator';
import { pricingCalculatorIntegrationsHash } from '../hooks/usePricingCalculatorIntegrationsMultiSelector';

const IntegrationCard = styled(Box)`
  position: relative;
  border: 1px solid
    ${({ theme }) => (theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.2)' : '#e0e0e0')};
  border-radius: 8px;
  padding: 20px;
  padding-top: 12px;
  margin-bottom: 16px;
  background-color: ${({ theme }) =>
    theme.palette.mode === 'dark' ? 'rgba(15, 23, 42, 0.85)' : '#ffffff'};
`;

const IntegrationHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const IntegrationTitleRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 28px; /* Leave space for remove button */
`;

const IntegrationBadges = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const IntegrationTitle = styled(Typography)`
  font-weight: 600;
  font-size: 1.1rem;
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#60a5fa' : '#2563eb')};
`;

const NewBadge = styled(Chip)`
  background-color: #fed7aa;
  color: #9a3412;
  font-size: 0.75rem;
  height: 24px;
`;

const MergeSupportedBadge = styled(Chip)`
  background-color: #d1fae5;
  color: #065f46;
  font-size: 0.75rem;
  height: 24px;
  font-weight: 500;
`;

const MergeNotSupportedBadge = styled(Chip)`
  background-color: #fee2e2;
  color: #991b1b;
  font-size: 0.75rem;
  height: 24px;
  font-weight: 500;
`;

const TierBadge = styled(Chip)`
  background-color: #e0e7ff;
  color: #3730a3;
  font-size: 0.75rem;
  height: 24px;
  font-weight: 600;
`;

const FieldRow = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

interface FieldLabelProps {
  $bold?: boolean;
}

const FieldLabel = styled(Typography)<FieldLabelProps>`
  font-size: 0.85rem;
  color: ${(props) =>
    props.theme.palette.mode === 'dark'
      ? props.$bold
        ? '#f1f5f9'
        : '#94a3b8'
      : props.$bold
        ? '#111827'
        : '#6b7280'};
  font-weight: ${(props) => (props.$bold ? 600 : 400)};
`;

const ReadOnlyField = styled(TextField)`
  width: 100%;

  & .MuiInputBase-root {
    background-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.6)' : '#f9fafb'};
  }

  & .MuiInputBase-input {
    color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#f1f5f9' : '#374151')} !important;
    font-weight: 600;
    -webkit-text-fill-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? '#f1f5f9' : '#374151'} !important;
  }
`;

const EditableField = styled(TextField)`
  width: 100%;

  & .MuiInputBase-root {
    background-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.8)' : '#ffffff'};
    border-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.3)' : 'rgba(0, 0, 0, 0.23)'};
  }

  & .MuiInputBase-input {
    color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#f1f5f9' : '#111827')} !important;
    font-weight: 600;
    -webkit-text-fill-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? '#f1f5f9' : '#111827'} !important;
  }

  & .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.3)' : 'rgba(0, 0, 0, 0.23)'};
  }

  & .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.5)' : 'rgba(0, 0, 0, 0.87)'};
  }

  & .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? '#60a5fa' : theme.palette.primary.main};
  }

  & .MuiInputAdornment-root {
    color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#94a3b8' : '#6b7280')};
  }
`;

const SliderContainer = styled(Box)`
  width: 100%;
  padding: 0 12px;
`;

const SliderLabel = styled(Typography)`
  font-size: 0.875rem;
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#94a3b8' : '#6b7280')};
  text-align: right;
  margin-top: 4px;
`;

const RemoveButton = styled(IconButton)`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px;
  color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#94a3b8' : '#9ca3af')};

  &:hover {
    color: ${({ theme }) => (theme.palette.mode === 'dark' ? '#ef4444' : '#dc2626')};
    background-color: ${({ theme }) =>
      theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(220, 38, 38, 0.1)'};
  }
`;

export interface PricingCalculatorIntegrationDetailProps {
  integrationKey: string;
  numberOfMembers: number;
  isRenewal: boolean;
  isMigration: boolean;
  isGovCloud: boolean;
  isSingleTenant: boolean;
  is4xDataTransfer: boolean;
  discountOnSetup: number;
  discountOnRecurring: number;
  pipelineOpportunities?: number;
  isNewlyAdded?: boolean;
  onMigrationChange: (integrationKey: string, isMigration: boolean) => void;
  onDiscountChange: (integrationKey: string, field: 'setup' | 'recurring', value: number) => void;
  onPipelineOpportunitiesChange?: (integrationKey: string, count: number) => void;
  onRemove?: (integrationKey: string) => void;
}

export const PricingCalculatorIntegrationDetail: React.FC<
  PricingCalculatorIntegrationDetailProps
> = ({
  integrationKey,
  numberOfMembers,
  isRenewal,
  isMigration,
  isGovCloud,
  isSingleTenant,
  is4xDataTransfer,
  discountOnSetup,
  discountOnRecurring,
  pipelineOpportunities = 1,
  isNewlyAdded = false,
  onMigrationChange,
  onDiscountChange,
  onPipelineOpportunitiesChange,
  onRemove,
}) => {
  const integration = pricingCalculatorIntegrationsHash[integrationKey];
  const metadata = INTEGRATION_METADATA[integrationKey];

  if (!integration || !metadata) return null;

  // Check if this is a first-time integration (not an existing integration)
  const isFirstTime = !metadata.existing_integration;

  // Calculate setup fee
  // Note: Newly added integrations during renewal should NOT get the renewal waiver
  const setupFee = calculateIntegrationSetupFee(
    integrationKey,
    numberOfMembers,
    isRenewal && !isNewlyAdded, // Only waive setup fee if renewal AND not newly added
    isMigration,
    0, // Don't apply discount here, we'll show it separately
    isSingleTenant,
    isFirstTime,
  );

  // For first-time integrations: discount applies to per-count fee
  // For existing integrations: discount applies to total fee
  const setupFeePerCount = isFirstTime ? setupFee / Math.max(1, pipelineOpportunities) : 0;
  const setupFeePerCountAfterDiscount = isFirstTime
    ? setupFeePerCount * (1 - discountOnSetup / 100)
    : 0;

  // For first-time: use per-count fee after discount as the final setup fee
  // For existing: use total fee after discount
  const setupFeeAfterDiscount = isFirstTime
    ? setupFeePerCountAfterDiscount
    : setupFee * (1 - discountOnSetup / 100);

  // Calculate recurring fee
  const recurringFee = calculateIntegrationRecurringFee(
    integrationKey,
    numberOfMembers,
    isGovCloud,
    isSingleTenant,
    is4xDataTransfer,
    0, // Don't apply discount here, we'll show it separately
  );

  const recurringFeeAfterDiscount = recurringFee * (1 - discountOnRecurring / 100);

  // Integration setup hours (GLX-1749): flat 5 hrs (<200 members) / 10 hrs (200+),
  // by team size — not derived from the dollar setup fee.
  const setupHours = calculateIntegrationSetupHours(numberOfMembers);

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const isSupportedByMerge = metadata?.supported_by_merge_dev || false;

  return (
    <IntegrationCard>
      {/* Remove Button */}
      {onRemove && (
        <Tooltip title="Remove integration" arrow placement="top">
          <RemoveButton
            size="small"
            onClick={() => onRemove(integrationKey)}
            aria-label="Remove integration"
          >
            <CloseIcon size={16} />
          </RemoveButton>
        </Tooltip>
      )}

      {/* Header */}
      <IntegrationHeader>
        <IntegrationTitleRow>
          <IntegrationTitle>{integration.name}</IntegrationTitle>
          <FormControlLabel
            control={
              <Switch
                checked={isMigration}
                onChange={(e) => onMigrationChange(integrationKey, e.target.checked)}
                size="small"
                color="warning"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MigrationIcon
                  size={14}
                  style={{
                    color: isMigration ? '#f59e0b' : 'inherit',
                    opacity: isMigration ? 1 : 0.5,
                  }}
                />
                <span>Migration</span>
              </Box>
            }
            labelPlacement="start"
            sx={{
              mr: 0,
              gap: 0.5,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.85rem',
                fontWeight: 500,
                color: (theme) =>
                  isMigration ? (theme.palette.mode === 'dark' ? '#fbbf24' : '#d97706') : 'inherit',
              },
            }}
          />
        </IntegrationTitleRow>
        <IntegrationBadges>
          <TierBadge label={`Complexity ${metadata.complexity_group}`} />
          {isNewlyAdded && isRenewal && <NewBadge label="Newly Added Integration" />}
          {isFirstTime && <NewBadge label="New Integration" />}
          {isSupportedByMerge ? (
            <MergeSupportedBadge label="Merge Supported" />
          ) : (
            <MergeNotSupportedBadge label="Not Merge Supported" />
          )}
        </IntegrationBadges>
      </IntegrationHeader>

      {/* Top Row - Pipeline Opportunities and Integration Fee (annual) */}
      {isFirstTime && onPipelineOpportunitiesChange && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
            },
            gap: 2,
            mb: 2,
            pb: 2,
            borderBottom: (theme) =>
              `1px solid ${
                theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.06)'
              }`,
          }}
        >
          {/* Pipeline Opportunities */}
          <FieldRow sx={{ mb: 0 }}>
            <FieldLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Pipeline opportunities
              <Tooltip
                title="Number of active sales opportunities in your pipeline that need this integration"
                arrow
                placement="top"
              >
                <Box component="span" sx={{ display: 'inline-flex', cursor: 'help' }}>
                  <HelpIcon size={14} />
                </Box>
              </Tooltip>
            </FieldLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Slider
                value={pipelineOpportunities}
                onChange={(_, value) =>
                  onPipelineOpportunitiesChange(integrationKey, value as number)
                }
                min={1}
                max={5}
                step={1}
                marks
                valueLabelDisplay="auto"
                color="primary"
                sx={{ flex: 1 }}
              />
              <Typography
                variant="body2"
                sx={{
                  minWidth: 40,
                  textAlign: 'right',
                  fontWeight: 600,
                  color: (theme) => (theme.palette.mode === 'dark' ? '#60a5fa' : '#2563eb'),
                }}
              >
                {pipelineOpportunities}
              </Typography>
            </Box>
          </FieldRow>

          {/* Integration Fee (annual) - shown at top for first-time */}
          <FieldRow sx={{ mb: 0 }}>
            <FieldLabel>Integration Fee (annual)</FieldLabel>
            <ReadOnlyField
              value={formatCurrency(recurringFee)}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                },
                htmlInput: {
                  readOnly: true,
                },
              }}
              variant="outlined"
              size="small"
            />
          </FieldRow>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '1fr 1fr',
          },
          gap: 3,
        }}
      >
        {/* Left Column - Setup Fee Section */}
        <Box>
          {/* Integration Setup Fee */}
          <FieldRow>
            <FieldLabel>Integration Setup Fee</FieldLabel>
            <ReadOnlyField
              value={formatCurrency(setupFee)}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                },
                htmlInput: {
                  readOnly: true,
                },
              }}
              variant="outlined"
              size="small"
            />
          </FieldRow>

          {/* Setup Fee per Count (for first-time integrations) */}
          {isFirstTime && (
            <>
              <FieldRow>
                <FieldLabel>Integration Setup Fee per count</FieldLabel>
                <ReadOnlyField
                  value={formatCurrency(setupFeePerCount)}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    },
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </FieldRow>

              {/* Discount on Setup Fee (for first-time, applies to per-count) */}
              <FieldRow>
                <FieldLabel $bold>Discount on Integration Setup Fee</FieldLabel>
                <EditableField
                  type="number"
                  value={discountOnSetup}
                  onChange={(e) =>
                    onDiscountChange(integrationKey, 'setup', parseFloat(e.target.value) || 0)
                  }
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    },
                    htmlInput: {
                      min: 0,
                      max: 100,
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </FieldRow>

              {/* Setup Fee per Count After Discount */}
              <FieldRow>
                <FieldLabel>Integration Setup Fee per count after discount</FieldLabel>
                <ReadOnlyField
                  value={formatCurrency(setupFeePerCountAfterDiscount)}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    },
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </FieldRow>
            </>
          )}

          {/* Discount on Setup Fee (for non-first-time integrations) */}
          {!isFirstTime && (
            <>
              <FieldRow>
                <FieldLabel $bold>Discount on Integration Setup Fee</FieldLabel>
                <EditableField
                  type="number"
                  value={discountOnSetup}
                  onChange={(e) =>
                    onDiscountChange(integrationKey, 'setup', parseFloat(e.target.value) || 0)
                  }
                  slotProps={{
                    input: {
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    },
                    htmlInput: {
                      min: 0,
                      max: 100,
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </FieldRow>

              {/* Setup Fee After Discount (only for non-first-time) */}
              <FieldRow>
                <FieldLabel>Integration Setup Fee after discount</FieldLabel>
                <ReadOnlyField
                  value={formatCurrency(setupFeeAfterDiscount)}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    },
                    htmlInput: {
                      readOnly: true,
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
              </FieldRow>
            </>
          )}

          {/* Integration Setup Hours (GLX-1749) */}
          <FieldRow>
            <FieldLabel sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Integration Setup Hours
              <Tooltip
                title="Estimated setup hours by team size: 5 hours for teams under 200 members, 10 hours for 200 or more."
                arrow
                placement="top"
              >
                <Box
                  component="span"
                  tabIndex={0}
                  role="img"
                  aria-label="Integration setup hours: 5 hours for teams under 200 members, 10 hours for 200 or more"
                  sx={{ display: 'inline-flex', cursor: 'help' }}
                >
                  <HelpIcon size={14} />
                </Box>
              </Tooltip>
            </FieldLabel>
            <ReadOnlyField
              value={`${setupHours}`}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                },
                htmlInput: {
                  readOnly: true,
                },
              }}
              variant="outlined"
              size="small"
            />
          </FieldRow>
        </Box>

        {/* Right Column - Recurring Fee Section */}
        <Box>
          {/* Integration Fee (annual) - only show here for non-first-time integrations */}
          {!isFirstTime && (
            <FieldRow>
              <FieldLabel>Integration Fee (annual)</FieldLabel>
              <ReadOnlyField
                value={formatCurrency(recurringFee)}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  },
                  htmlInput: {
                    readOnly: true,
                  },
                }}
                variant="outlined"
                size="small"
              />
            </FieldRow>
          )}

          {/* Discount on Annual Fee */}
          <FieldRow>
            <FieldLabel $bold>Discount on annual integration fee</FieldLabel>
            <EditableField
              type="number"
              value={discountOnRecurring}
              onChange={(e) =>
                onDiscountChange(integrationKey, 'recurring', parseFloat(e.target.value) || 0)
              }
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
                htmlInput: {
                  min: 0,
                  max: 100,
                },
              }}
              variant="outlined"
              size="small"
            />
          </FieldRow>

          {/* Annual Fee After Discount */}
          <FieldRow>
            <FieldLabel>Integration fee (annual) after discount</FieldLabel>
            <ReadOnlyField
              value={formatCurrency(recurringFeeAfterDiscount)}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                },
                htmlInput: {
                  readOnly: true,
                },
              }}
              variant="outlined"
              size="small"
            />
          </FieldRow>
        </Box>
      </Box>
    </IntegrationCard>
  );
};
